import type { ActivityCompletionMetrics } from "@/app/integration/contracts";
import { getDailyRoutineFeedbackCue } from "@/audio/curriculum-catalog/habits-feedback-cues";
import { createLevelDefinitionSchema } from "@/domain/schemas";
import {
  createSequenceState,
  getSequenceHint,
  isSequenceComplete,
  reduceSequence,
  sequencePayloadSchema,
  type SequencePayload
} from "@/game/templates/sequence";

import {
  requireActivityType,
  requireHabitsAudioPath,
  requirePlayerMedia
} from "./binding-utils";
import type {
  DailyRoutineAction,
  DailyRoutineBinding,
  DailyRoutineState,
  HabitsPlayerLevel
} from "./types";

const dailyRoutineLevelSchema = createLevelDefinitionSchema(
  sequencePayloadSchema
);

function getItem(payload: SequencePayload, itemId: string) {
  const item = payload.items.find(({ id }) => id === itemId);

  if (item === undefined) {
    throw new Error("Routine order references missing item " + itemId);
  }

  return item;
}

function createState(payload: SequencePayload): DailyRoutineState {
  const arrangedForPlay: SequencePayload = {
    ...payload,
    items: [...payload.items].reverse()
  };

  return {
    attemptCount: 0,
    completed: false,
    feedbackKind: null,
    feedbackPlaybackRequest: 0,
    sequence: createSequenceState(arrangedForPlay)
  };
}

function reduce(
  state: DailyRoutineState,
  payload: SequencePayload,
  action: DailyRoutineAction
): DailyRoutineState {
  if (action.type === "replay") {
    return createState(payload);
  }

  if (state.completed) {
    return state;
  }

  if (action.type === "check-order") {
    const attemptCount = state.attemptCount + 1;

    if (isSequenceComplete(state.sequence)) {
      return {
        ...state,
        attemptCount,
        completed: true,
        feedbackKind: null,
        feedbackPlaybackRequest: state.feedbackPlaybackRequest
      };
    }

    const retried = reduceSequence(state.sequence, { type: "retry" });
    const hinted = reduceSequence(retried, { type: "request-hint" });

    return {
      ...state,
      attemptCount,
      feedbackKind: "retry",
      feedbackPlaybackRequest: state.feedbackPlaybackRequest + 1,
      sequence: hinted
    };
  }

  return {
    ...state,
    feedbackKind: action.type === "request-hint" ? "hint" : null,
    feedbackPlaybackRequest:
      action.type === "request-hint"
        ? state.feedbackPlaybackRequest + 1
        : state.feedbackPlaybackRequest,
    sequence: reduceSequence(state.sequence, action)
  };
}

function getFeedbackCue(
  state: DailyRoutineState,
  payload: SequencePayload,
  levelId: string
) {
  const hint = getSequenceHint(state.sequence);

  if (hint === null || state.feedbackKind === null) {
    return null;
  }

  return getDailyRoutineFeedbackCue(
    levelId,
    payload,
    state.feedbackKind,
    hint
  );
}

function getHintedItemIds(state: DailyRoutineState): Set<string> {
  const hint = getSequenceHint(state.sequence);

  if (hint === null || state.feedbackKind === null) {
    return new Set();
  }

  switch (hint.stage) {
    case "gentle-hint": {
      const itemId = state.sequence.order[hint.targetIndex];
      return itemId === undefined ? new Set() : new Set([itemId]);
    }
    case "reduced-choices":
      return new Set(hint.candidateItemIds);
    case "replay-demonstration":
      return new Set(hint.order);
  }
}

function getMetrics(
  state: DailyRoutineState
): ActivityCompletionMetrics {
  const correctRate =
    state.completed && state.attemptCount > 0
      ? 1 / state.attemptCount
      : 0;

  return {
    completed: state.completed,
    score: Math.round(correctRate * 100),
    correctRate,
    hintCount: state.sequence.hintState.hintCount,
    retryCount: state.sequence.retryCount
  };
}

function createAdapter(
  level: HabitsPlayerLevel<SequencePayload>
): DailyRoutineBinding["adapter"] {
  return {
    activityType: "daily-routine",
    parsePayload: (value) => sequencePayloadSchema.parse(value),
    createInitialState: (payload, context) => {
      void context;
      return createState(payload);
    },
    reduce: (state, payload, action, context) => {
      void context;
      return reduce(state, payload, action);
    },
    getMetrics: (state, payload) => {
      void payload;
      return getMetrics(state);
    },
    toRenderModel: (state, payload) => {
      const metrics = getMetrics(state);
      const hintedItemIds = getHintedItemIds(state);
      const lastIndex = state.sequence.order.length - 1;
      const feedbackCue = getFeedbackCue(state, payload, level.id);

      return {
        difficulty: level.difficulty,
        instruction: {
          audioPath: level.promptAudio,
          caption: level.instruction
        },
        levelId: level.id,
        sceneImagePath: level.assets[0],
        title: level.title,
        phase: state.completed ? "result" : "play",
        items: state.sequence.order.map((itemId, index) => {
          const item = getItem(payload, itemId);

          return {
            audioPath: requireHabitsAudioPath(
              item.audio,
              level.id,
              "routine item " + item.id
            ),
            canMoveLater: !state.completed && index < lastIndex,
            canMoveEarlier: !state.completed && index > 0,
            focused: state.sequence.focusedItemId === itemId,
            hinted: hintedItemIds.has(itemId),
            id: itemId,
            label: item.label,
            position: index + 1
          };
        }),
        feedback:
          feedbackCue === null || state.feedbackKind === null
            ? null
            : {
                audioPath: feedbackCue.audioPath,
                kind: state.feedbackKind,
                playbackRequest: state.feedbackPlaybackRequest,
                text: feedbackCue.text
              },
        result: state.completed ? metrics : null
      };
    }
  };
}

export function createDailyRoutineBinding(
  sourceLevel: unknown
): DailyRoutineBinding {
  const level = requirePlayerMedia(
    dailyRoutineLevelSchema.parse(sourceLevel)
  );
  requireActivityType(level, "daily-routine");

  return {
    activityType: "daily-routine",
    level,
    adapter: createAdapter(level)
  };
}
